import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { SessionNoteFormRegistry } from '../sessionNote/sessionNoteFormRegistry';
import { AssistantTargetRegistry } from './assistantTargetRegistry';
import {
  AssistantUiContext,
  AssistantUiStateContext,
  type AssistantSpotlight,
  type AssistantUiContextValue,
  type AssistantUiStateContextValue,
} from './assistantUiContext';
import { isPageEditEvent } from './pageEditTracking';

const EDIT_EVENT_NAMES = ['input', 'change'] as const;

export function AssistantUiProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [registry] = useState(function createRegistry() {
    return new AssistantTargetRegistry();
  });
  const [sessionNoteForms] = useState(function createSessionNoteForms() {
    return new SessionNoteFormRegistry();
  });
  const [spotlight, setSpotlight] = useState<AssistantSpotlight | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const hasEditsRef = useRef(false);
  const locationKey = location.pathname + location.search;

  useEffect(
    function resetEditsAfterNavigation() {
      hasEditsRef.current = false;
    },
    [locationKey]
  );

  useEffect(function trackPageEdits() {
    function rememberEdit(event: Event) {
      if (isPageEditEvent(event)) {
        hasEditsRef.current = true;
      }
    }
    EDIT_EVENT_NAMES.forEach(function listen(eventName) {
      document.addEventListener(eventName, rememberEdit, true);
    });
    return function stopTracking() {
      EDIT_EVENT_NAMES.forEach(function unlisten(eventName) {
        document.removeEventListener(eventName, rememberEdit, true);
      });
    };
  }, []);

  const clearSpotlight = useCallback(function clear() {
    setSpotlight(null);
  }, []);
  const announce = useCallback(function announceMessage(message: string) {
    setAnnouncement('');
    window.requestAnimationFrame(function publishAnnouncement() {
      setAnnouncement(message);
    });
  }, []);
  const hasEditsOnCurrentPage = useCallback(function readEdits() {
    return hasEditsRef.current;
  }, []);

  const actions = useMemo(
    function buildActions(): AssistantUiContextValue {
      return {
        registry,
        sessionNoteForms,
        showSpotlight: setSpotlight,
        clearSpotlight,
        announce,
        hasEditsOnCurrentPage,
      };
    },
    [registry, sessionNoteForms, clearSpotlight, announce, hasEditsOnCurrentPage]
  );
  const state = useMemo(
    function buildState(): AssistantUiStateContextValue {
      return { spotlight, announcement };
    },
    [spotlight, announcement]
  );

  return (
    <AssistantUiContext.Provider value={actions}>
      <AssistantUiStateContext.Provider value={state}>{children}</AssistantUiStateContext.Provider>
    </AssistantUiContext.Provider>
  );
}
